import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useLocation, Link } from 'react-router-dom'
import { debounce } from 'lodash'
import { Container, Input, Row, Col, Button } from 'reactstrap'
import branditRails from '../../api/brandit_rails'
import { isValidEmail } from '../../util/utils'
import { loginUrlFor, withJamParams } from '../../util/recordingNavigation'

/** Guest checkout email input with sign-in option. Returns null for logged-in users. */
const GuestCheckoutPanel = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const portal = useSelector(state => state.portal)
  const currentUser = useSelector(state => state.currentUser)
  const contactEmail = useSelector(state => state.checkout.contactEmail)

  // Track the last contactEmail we synced from Redux to avoid overwriting user edits
  const lastSyncedEmailRef = useRef(contactEmail)

  const [email, setEmail] = useState(contactEmail || '')
  const [emailValid, setEmailValid] = useState(Boolean(contactEmail) && isValidEmail(contactEmail))
  const [touched, setTouched] = useState(false)
  const [userExistsOnPortal, setUserExistsOnPortal] = useState(false)

  const signInUrl = useMemo(() => {
    const rawLoginPath = (portal?.custom_login_url || 'log-in').trim()
    const loginPath = rawLoginPath || 'log-in'
    const normalizedLoginPath = loginPath.startsWith('/') ? loginPath : `/${loginPath}`
    return loginUrlFor(normalizedLoginPath, withJamParams('/cart', location.search))
  }, [portal?.custom_login_url, location.search])

  // Only sync from Redux when contactEmail changes externally (e.g., restored from session)
  // Don't sync when the user is actively editing (which would overwrite their changes)
  useEffect(() => {
    if (contactEmail && contactEmail !== lastSyncedEmailRef.current) {
      lastSyncedEmailRef.current = contactEmail
      setEmail(contactEmail)
      setEmailValid(isValidEmail(contactEmail))
    }
  }, [contactEmail])

  const debouncedCheckEmailExists = useMemo(
    () =>
      debounce(async emailToCheck => {
        if (!emailToCheck || !isValidEmail(emailToCheck)) {
          setUserExistsOnPortal(false)
          return
        }
        try {
    const formData = new FormData()
          formData.append('email', emailToCheck)
          const response = await branditRails.post('/users/check_guest_email', formData)
      setUserExistsOnPortal(response.data.exists)
        } catch {
          setUserExistsOnPortal(false)
        }
      }, 300),
    []
  )

  useEffect(() => {
    return () => debouncedCheckEmailExists.cancel()
  }, [debouncedCheckEmailExists])

  const handleEmailChange = useCallback(
    e => {
      const newEmail = e.target.value
      setTouched(true)
      setEmail(newEmail)

      const isValid = isValidEmail(newEmail)
      setEmailValid(isValid)

      dispatch({ type: 'SET_CHECKOUT_ERROR', payload: { contactEmail: !isValid } })

      if (isValid) {
        dispatch({ type: 'SET_CONTACT_EMAIL', payload: newEmail })
        // Keep ref in sync so the useEffect doesn't overwrite user's valid input
        lastSyncedEmailRef.current = newEmail
      }

      debouncedCheckEmailExists(newEmail)
    },
    [dispatch, debouncedCheckEmailExists]
  )

  const handleSignInClick = () => {
    history.push(signInUrl)
  }

  if (currentUser?.id) {
    return null
  }

  return (
    <div className="box-shadow white-background mf-m-0">
      <div className="ckout-header">
        <h5 className="mf-m-0">Checkout</h5>
      </div>
      <Container className="mf-mb-4">
        <Row className="align-items-start mf-p-3">
          <Col xs="12" lg="5">
            <p>
              Prefer guest checkout? <br />
              Simply enter your email for confirmation and tracking.
            </p>
            <Input
              data-jam-blur
              placeholder="Contact Email"
              name="contact_email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              className={!emailValid && touched ? 'is-invalid' : ''}
            />
            {!emailValid && touched && <div className="text-danger small mf-mt-1">Please enter a valid email.</div>}
            {userExistsOnPortal && (
              <div className="text-success small mf-mt-1">
                We found an account associated with this email.{' '}
                <Link to={signInUrl}>Click here to sign in</Link> and save this order to your account.
              </div>
            )}
          </Col>
          <Col xs="12" lg="2" className="mf-text-center text-muted align-self-center mf-my-3 my-lg-0">
            <small>- or -</small>
          </Col>
          <Col xs="12" lg="5">
            <p>Sign in to use your saved addresses & credits, and to store this order in your account.</p>
            <Button className="w-100" color="secondary" onClick={handleSignInClick}>
              Sign In
            </Button>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default GuestCheckoutPanel
